import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Check, Loader2, BookOpen, Award, MessageSquareText, ArrowRight, Clock, Info, CreditCard, ShieldCheck, FileText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LICENSE_PRODUCTS, FAIR_USE_NOTE } from "@/lib/products";
import { usePricing, aiExtensions, licensePrice } from "@/lib/pricing";
import InvoiceOrderDialog from "@/components/InvoiceOrderDialog";

const BENEFITS = [
  { icon: BookOpen, text: "Alle 10 Module mit interaktiven Erklärungen, Grafiken & Quiz" },
  { icon: MessageSquareText, text: "Voller KI-Licht-Coach + Briefing-Generator – 30 Tage inklusive" },
  { icon: Award, text: "Abschluss-Zertifikat mit Verifizierungslink & LinkedIn-Eintrag" },
];

export default function LicenseUpsell({ feature, title = "Schalten Sie den vollständigen Kurs frei" }) {
  const { user } = useAuth();
  const pricing = usePricing();
  const [plan, setPlan] = useState("einzel");
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  const product = LICENSE_PRODUCTS.find((p) => p.id === plan) || LICENSE_PRODUCTS[0];
  const productPrice = licensePrice(pricing, plan);
  const qty = Number(quantity) || 1;

  const intro = feature
    ? `${feature} ist Teil des vollen Kurszugangs.`
    : "Sie nutzen gerade den Gratis-Test mit Modul 1. Schalten Sie alle 10 Module frei.";

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "license",
        plan,
        quantity: qty,
        origin_url: window.location.origin,
        cancel_path: window.location.pathname,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="license-upsell"
      className="relative overflow-hidden rounded-3xl glass-strong p-6 sm:p-10"
    >
      <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
      <div className="relative">
        <p className="text-[11px] uppercase tracking-[0.18em] text-gold/80 mb-3">LUX Lighting Academy · Vollzugang</p>
        <div className="relative inline-flex h-14 w-14 rounded-2xl bg-gold/15 border border-gold/30 items-center justify-center mb-5">
          <Sparkles className="h-7 w-7 text-gold" />
          <div className="absolute inset-0 blur-xl bg-gold/20 -z-10 rounded-2xl" />
        </div>
        <h2 className="font-heading font-extrabold text-2xl sm:text-3xl leading-tight">{title}</h2>
        <p className="text-foreground/70 mt-3 leading-relaxed max-w-2xl">{intro}</p>

        <ul className="mt-6 grid sm:grid-cols-3 gap-3">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <li key={i} className="flex items-start gap-3 text-sm text-foreground/85 rounded-xl bg-white/5 border border-white/10 p-3.5">
                <span className="mt-0.5 h-6 w-6 flex-shrink-0 rounded-lg bg-gold/15 border border-gold/25 flex items-center justify-center">
                  <Icon className="h-3.5 w-3.5 text-gold" />
                </span>
                {b.text}
              </li>
            );
          })}
        </ul>

        <div className="mt-8 grid lg:grid-cols-2 gap-8">
          {/* Left: Lizenz wählen + Info */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-3">1 · Lizenz wählen</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {LICENSE_PRODUCTS.map((p) => {
                const active = plan === p.id;
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => setPlan(p.id)}
                    data-testid={`license-option-${p.id}`}
                    aria-pressed={active}
                    className={`w-full text-left p-4 rounded-xl border transition-all duration-300 ${
                      active ? "bg-gold/15 border-gold/50" : "bg-white/5 border-white/10 hover:bg-white/[0.08]"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-heading font-bold">{p.name}</span>
                      <span className={`h-4 w-4 rounded-full border flex items-center justify-center ${active ? "border-gold bg-gold text-[#0a0d12]" : "border-white/30"}`}>
                        {active && <Check className="h-3 w-3" />}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{p.seats}</div>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className="font-heading font-bold text-gold text-lg">{licensePrice(pricing, p.id).price}</span>
                      <span className="text-[11px] text-muted-foreground">exkl. MWST</span>
                    </div>
                    <p className="text-xs text-foreground/70 mt-2 leading-relaxed">{p.desc}</p>
                    {p.note && <p className="text-[11px] text-foreground/55 mt-2 italic leading-relaxed">{p.note}</p>}
                    <span className={`mt-3 inline-block text-xs font-medium ${active ? "text-gold" : "text-muted-foreground"}`}>
                      {active ? "✓ Ausgewählt" : p.selectLabel}
                    </span>
                  </button>
                );
              })}
            </div>

            <label className="block mt-4">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Anzahl Lizenzen</span>
              <input
                type="number"
                min={1}
                max={99}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                data-testid="license-quantity"
                className="mt-1.5 w-28 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-gold/50 focus:ring-2 focus:ring-gold/20 transition-all text-foreground"
              />
            </label>

            {/* Info: KI-Coach */}
            <div className="mt-6 rounded-xl bg-white/5 border border-white/10 p-4" data-testid="ki-coach-info">
              <div className="flex items-center gap-2 mb-2">
                <Info className="h-4 w-4 text-gold" />
                <h4 className="font-heading font-bold text-sm">So funktioniert der KI-Coach</h4>
              </div>
              <ul className="space-y-1.5 text-xs text-foreground/75 leading-relaxed list-disc pl-4">
                <li>Die <strong>Einzel-Lizenz</strong> beinhaltet den vollen Kurszugang für 1 Person und 30 Tage KI-Coach.</li>
                <li>Die <strong>Team-Lizenz</strong> beinhaltet den vollen Kurszugang für bis zu 10 Personen und 30 Tage KI-Coach pro Teammitglied.</li>
                <li>Nach Ablauf der ersten 30 Tage bleibt der <strong>Kurszugang dauerhaft bestehen</strong>.</li>
                <li>Nur der <strong>KI-Coach</strong> läuft nach 30 Tagen ab und kann verlängert werden.</li>
                <li>Bei der <strong>Team-Lizenz</strong> wird die KI-Laufzeit <strong>zentral über das Büro</strong> verwaltet – Teammitglieder verlängern nicht einzeln.</li>
              </ul>
              <div className="mt-3">
                <p className="text-xs font-medium text-foreground/80 mb-2">KI-Coach verlängern – Pakete {plan === "team" ? "(Team)" : "(Einzel)"}:</p>
                <div className="flex flex-wrap gap-2">
                  {aiExtensions(pricing, plan).map((x) => (
                    <span key={x.days} data-testid={`ai-extension-${x.days}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gold/10 border border-gold/25 text-xs">
                      <Clock className="h-3 w-3 text-gold" /> {x.days} Tage · <span className="text-gold">{x.price}</span>
                    </span>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-foreground/55 mt-3 leading-relaxed">{FAIR_USE_NOTE}</p>
            </div>
          </div>

          {/* Right: Bezahlung */}
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5 sm:p-6 self-start">
            <h3 className="font-heading font-bold text-lg mb-1">2 · {plan === "team" ? "Auf Rechnung bestellen" : "Online bezahlen"}</h3>
            <p className="text-sm text-foreground/65 mb-4">
              {plan === "team"
                ? "Für Büros: Wir erstellen eine Schweizer QR-Rechnung (zahlbar innert 14 Tagen). Ihr Vollzugang wird sofort freigeschaltet."
                : "Bezahlen Sie sicher per Karte. Ihr Vollzugang wird sofort nach der Zahlung freigeschaltet."}
            </p>

            <div className="rounded-xl bg-gold/10 border border-gold/25 p-4" data-testid="license-summary">
              <div className="flex items-center gap-2 text-sm">
                <Sparkles className="h-4 w-4 text-gold flex-shrink-0" />
                <span><strong>{product.name}</strong> · {product.seats}</span>
              </div>
              <div className="mt-3 flex items-end justify-between">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">Gesamt ({qty} ×)</span>
                <span className="font-heading font-extrabold text-2xl text-gold" data-testid="license-total">
                  {productPrice.price}{qty > 1 ? ` × ${qty}` : ""}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1 text-right">exkl. MWST · zzgl. MWST auf Rechnung</p>
            </div>

            {plan === "einzel" ? (
              <>
                <button
                  onClick={checkout}
                  disabled={loading}
                  data-testid="license-checkout-button"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                  {loading ? "Weiterleitung zur Zahlung…" : "Jetzt online bezahlen"}
                  {!loading && <ArrowRight className="h-4 w-4" />}
                </button>
                <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
                  <ShieldCheck className="h-3.5 w-3.5 text-gold" /> Sichere Zahlung über Stripe
                </p>
              </>
            ) : (
              <>
                <button
                  onClick={() => setInvoiceOpen(true)}
                  data-testid="license-invoice-button"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300"
                >
                  <FileText className="h-4 w-4" /> Auf Rechnung bestellen (QR · 14 Tage)
                  <ArrowRight className="h-4 w-4" />
                </button>
                <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
                  <ShieldCheck className="h-3.5 w-3.5 text-gold" /> Schweizer QR-Rechnung via AbaNinja · Zugang sofort freigeschaltet
                </p>
              </>
            )}
          </div>
        </div>
      </div>
      <InvoiceOrderDialog open={invoiceOpen} onOpenChange={setInvoiceOpen} quantity={qty} />
    </motion.div>
  );
}
